/**
 * A simplified Recipient Header, found on the top of the PreviewPage,
 * displays a recipient thumbnail (if available) and the user name
 * (if available) and the user id.
 */

import PropTypes from 'prop-types'
import React from 'react'
import Media from 'react-media'
import { SIZE_MOBILE } from 'lib/styles/media-queries'
import { connect } from 'transfer/utils/p2p-connect'
import { getFlowType } from 'transfer/utils/url-utils'
import glamorous from 'glamorous'

import ContactThumbnail from 'component-thumbnail'
import { Text, HeadingText, BodyText } from '@paypalcorp/pp-react'

import { isDw2Repaint } from 'experiments/dw2Repaint'

import ContactAvatar from 'transfer/components/contactAvatar'

import { getAvatarColor } from 'lib/avatar'

/*
 *  Helper Classes and Glamorous Styling
 */
const HeaderContainer = glamorous.div({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '1rem',
  justifyContent: 'center',
  textAlign: 'left',
})

const RecipientContainer = glamorous.div('recipientHeader', {
  background: '#ffffff',
  textAlign: 'center',
  borderRadius: 5,
  display: 'inline-flex',
})

const DetailsContainer = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '16px',
  position: 'relative',
  wordBreak: 'break-word',
})

const PrimaryInfoText = props => <Text size="xl2" regular {...props} />
const PrimaryInfo = glamorous(PrimaryInfoText)({
  margin: '0',
  position: 'relative',
  lineHeight: '1.6rem',
  fontWeight: 'normal',
  fontSize: 20,
})

const SecondaryInfoText = props => <Text size="lg" {...props} />
const SecondaryInfo = glamorous(SecondaryInfoText)({
  margin: '0',
  color: '#6c7378',
  position: 'relative',
  lineHeight: '1.6rem',
  fontWeight: 'normal',
  fontSize: 16,
})

const TertiaryInfo = SecondaryInfo

export class SimpleRecipientHeader extends React.Component {
  static propTypes = {
    thumbnailUrl: PropTypes.string,
    firstInitial: PropTypes.string,
    fullName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    alternateFullName: PropTypes.string,
    lastInitial: PropTypes.string,
    showRequestLabel: PropTypes.bool,
    isCharity: PropTypes.bool,
    recipientDisplayDetails: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    shouldRenderThumbnail: PropTypes.bool,
    recipient: PropTypes.string,
    networkDisplayName: PropTypes.string,
    networkDisplayAddress: PropTypes.string,
    styleOverrides: PropTypes.object,
    showLocation: PropTypes.bool,
    thumbnailDiameter: PropTypes.number,
    unilateralFullName: PropTypes.string,
  }

  static defaultProps = {
    showLocation: false,
  }

  render() {
    const recipient =
      this.props.unilateralFullName ||
      this.props.displayName ||
      this.props.recipientDisplayDetails ||
      this.props.recipient
    const shouldShowRecipientDisplayDetails =
      this.props.unilateralFullName ||
      this.props.firstInitial ||
      this.props.fullName ||
      this.props.alternateFullName ||
      this.props.networkDisplayName

    const {
      styleOverrides = {},
      showLocation,
      networkDisplayAddress,
      thumbnailDiameter,
      firstName,
      lastName,
      firstInitial,
      lastInitial,
      alternateFullName,
      thumbnailUrl,
    } = this.props

    const shouldShowLocation = showLocation && networkDisplayAddress

    return (
      <HeaderContainer css={styleOverrides.headerContainer}>
        <RecipientContainer css={styleOverrides.recipientContainer}>
          {isDw2Repaint()
            ? this.props.shouldRenderThumbnail && (
                <Media query={`(max-width: ${SIZE_MOBILE}px)`}>
                  {matches => (
                    <ContactAvatar
                      size={matches ? 'md' : 'lg'}
                      imageUrl={thumbnailUrl}
                      firstInitial={firstInitial}
                      lastInitial={lastInitial}
                      isBusiness={Boolean(alternateFullName)}
                      color={getAvatarColor({ firstName, lastName })}
                    />
                  )}
                </Media>
              )
            : this.props.shouldRenderThumbnail && (
                <ContactThumbnail
                  preset="modern"
                  thumbnailUrl={this.props.thumbnailUrl}
                  firstInitial={this.props.firstInitial}
                  lastInitial={this.props.lastInitial}
                  diameter={thumbnailDiameter}
                />
              )}
        </RecipientContainer>

        <DetailsContainer css={styleOverrides.detailsContainer}>
          {isDw2Repaint() ? (
            <HeadingText size="sm">{recipient}</HeadingText>
          ) : (
            <PrimaryInfo
              className="ppvx_text--hero"
              css={styleOverrides.primaryInfo}
            >
              {recipient}
            </PrimaryInfo>
          )}
          {isDw2Repaint()
            ? shouldShowRecipientDisplayDetails && (
                <BodyText>
                  {getFlowType() === 'payRequest'
                    ? this.props.recipient
                    : this.props.recipientDisplayDetails}
                </BodyText>
              )
            : shouldShowRecipientDisplayDetails && (
                <SecondaryInfo
                  className="ppvx_text--hero"
                  css={styleOverrides.secondaryInfo}
                >
                  {getFlowType() === 'payRequest'
                    ? this.props.recipient
                    : this.props.recipientDisplayDetails}
                </SecondaryInfo>
              )}
          {shouldShowLocation && (
            <TertiaryInfo css={styleOverrides.tertiaryInfo}>
              {networkDisplayAddress}
            </TertiaryInfo>
          )}
        </DetailsContainer>
      </HeaderContainer>
    )
  }
}

export default connect(
  ({ server, recipient, unilateral }) => ({
    shouldRenderThumbnail: server.env !== 'ppme' && server.env !== 'pools',
    recipient: recipient.recipient,
    displayName: recipient.displayName,
    recipientDisplayDetails: recipient.recipientDisplayDetails,
    thumbnailUrl: recipient.thumbnailUrl,
    firstInitial: recipient.firstInitial,
    fullName: recipient.fullName,
    firstName: recipient.firstName,
    lastName: recipient.lastName,
    alternateFullName: recipient.alternateFullName,
    lastInitial: recipient.lastInitial,
    isCharity: recipient.isCharity,
    networkDisplayName: recipient.networkDisplayName,
    networkDisplayAddress: recipient.networkDisplayAddress,
    unilateralFullName: unilateral.name,
  }),
  null,
  (stateProps, dispatchProps, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
  })
)(SimpleRecipientHeader)
